import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { ReconciliationInvoice, UpdateInvoicePaymentPayload } from './types';

const getEndpoint = (invoiceId: number) => `ums/v1/cms/reconciliation/invoices/${invoiceId}/payment`;

type TData = ReconciliationInvoice;
type TError = AxiosError;
type TVariables = UpdateInvoicePaymentPayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** Marks an order-scope invoice paid/unpaid (400 on monthly scope; allowed on closed months —
 * collections happen after close). Omitting `paidNote` preserves the stored note. */
export function useUpdateInvoicePaymentApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ invoiceId, isPaid, paidNote }: TVariables) => {
    try {
      const { data } = await http.patch(getEndpoint(invoiceId), {
        isPaid,
        ...(paidNote !== undefined ? { paidNote } : {}),
      });
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { ...options, mutationKey: ['reconciliation-invoice-payment'] });
}
